// Menu — a config-driven dropdown-menu primitive, pure.
//
// A thin wrapper over @radix-ui/react-dropdown-menu: Radix owns the hard
// a11y (menu/menuitem roles, aria-haspopup/expanded wiring on the trigger,
// arrow-key navigation, typeahead, Escape-to-close, focus return, portal +
// collision handling) so we never hand-roll any of it. This file spends its
// effort on the shape of the public surface and on the look (foundations v1).
//
// The menu is described by DATA, not children: `items` is a readonly list of
// a discriminated union (types over raw objects — every kind is its own named
// leaf type, summed into MenuItem, discriminated by `_tag`). Distinct kinds,
// not a flag: a separator and a section-label are their own tags, never a
// boolean on the item shape. Rendering switches on `_tag` exhaustively — add
// a kind and the switch stops compiling until it is handled.
//
// Register (.claude/research/design/06): near-monochrome. The content is an elevated
// plane — the border→elevation discipline (§5): a soft shadow on the elevated
// ground carries "this floats", not a painted 1px edge. The accent is NOT
// spent here (§1) — a menu is neutral chrome; highlight is a neutral ink
// tint. The one sanctioned hairline is the separator, which DIVIDES two
// co-planar item groups.
//
// Colour is never the sole channel (SC 1.4.1): a `danger`-tone item is red,
// but the destructive intent is carried by the item's own label text (a
// non-colour channel) — the colour reinforces meaning the label already
// states. This matches the shipped Button `danger` variant's standing.
//
// Pure/props-driven: no I/O, no state, no domain knowledge. Every selection
// is reported UP via the item's own `onSelect` callback. The `trigger` is a
// single interactive element (a Button/IconButton) rendered via Radix
// `asChild`, so it keeps its own focus ring and receives the menu's aria
// wiring — never a nested button.

import * as RadixMenu from "@radix-ui/react-dropdown-menu";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import "./Menu.css";

// The tone of an action item. A plain enum driving the look (like
// ButtonVariant), not a flag: `danger` is a quiet destructive action.
export type MenuItemTone = "default" | "danger";

// An actionable row. Selecting it fires `onSelect` and closes the menu.
export type MenuActionItem = {
    readonly _tag: "item";
    readonly label: ReactNode;
    // Optional leading glyph; inherits currentColor, decorative to AT.
    readonly icon?: ReactNode;
    readonly onSelect: () => void;
    readonly disabled?: boolean;
    readonly tone?: MenuItemTone;
    // A one-line explanation under the label.
    readonly description?: ReactNode;
    // The current choice (a remembered kind): a check at the row's end.
    readonly checked?: boolean;
};

// A hairline that DIVIDES two co-planar groups of items (the sanctioned
// border use, §5). Carries no content.
export type MenuSeparator = {
    readonly _tag: "separator";
};

// A non-interactive caption naming the group of items beneath it.
export type MenuLabel = {
    readonly _tag: "label";
    readonly label: ReactNode;
};

// A row that opens a NESTED menu (Radix's `Sub`) rather than firing an
// action itself — e.g. an attach menu's "choose an existing source" item
// revealing the real pick list. Recursive: a submenu's own items are the
// same `MenuItem` sum, so it can nest again if a caller ever needs to.
export type MenuSubmenuItem = {
    readonly _tag: "submenu";
    readonly label: ReactNode;
    // Optional leading glyph; inherits currentColor, decorative to AT.
    readonly icon?: ReactNode;
    readonly items: readonly MenuItem[];
    readonly disabled?: boolean;
    // The current choice, shown dim before the chevron ("English").
    readonly hint?: ReactNode;
};

// A row picking one of a few options, drawn as a segmented control (theme:
// Light / Dark). Each option is a radio item, so arrow keys reach it.
type MenuSegmentedItem = {
    readonly _tag: "segmented";
    readonly label: ReactNode;
    readonly icon?: ReactNode;
    readonly options: readonly {
        readonly value: string;
        readonly label: ReactNode;
    }[];
    readonly value: string;
    readonly onChange: (value: string) => void;
};

// The sum: everything a menu row can be, discriminated by `_tag`.
export type MenuItem =
    | MenuActionItem
    | MenuSeparator
    | MenuLabel
    | MenuSubmenuItem
    | MenuSegmentedItem;

// Which trigger edge the content aligns to (genuine positional config, a
// named union — Radix's own `align` domain, not a flag).
export type MenuAlign = "start" | "center" | "end";
type MenuSide = "top" | "right" | "bottom" | "left";

export type MenuProps = {
    // A single interactive element (Button/IconButton). Rendered as the menu
    // trigger via `asChild`, so the aria wiring lands on it, not a wrapper.
    readonly trigger: ReactNode;
    readonly items: readonly MenuItem[];
    readonly align?: MenuAlign;
    // Which side of the trigger it opens on (it flips if it would overflow).
    readonly side?: MenuSide;
    // Controlled: open from outside the trigger too (a key).
    readonly open?: boolean;
    // Reports open/close UP so a host can lazily load menu contents the first
    // time it opens (e.g. fetch the workspace list for a submenu). Pure config —
    // the parent decides; no state kept here.
    readonly onOpenChange?: (open: boolean) => void;
    // A class on the floating content, for a surface that sizes its menus
    // to its own design (the left menu's row and view menus).
    readonly contentClassName?: string;
};

// The gap between trigger and content, in px. Radix positions the portalled
// content with a numeric offset (it cannot read a CSS var), so this mirrors
// --space-1 (4px) at the one unavoidable JS/CSS boundary.
const SIDE_OFFSET = 4;

const renderItem = (item: MenuItem, index: number) => {
    switch (item._tag) {
        case "separator":
            return (
                <RadixMenu.Separator
                    key={index}
                    className="bilda-menu__separator"
                />
            );
        case "label":
            return (
                <RadixMenu.Label key={index} className="bilda-menu__label">
                    {item.label}
                </RadixMenu.Label>
            );
        case "item":
            return (
                <RadixMenu.Item
                    key={index}
                    className={`bilda-menu__item${
                        item.tone === "danger"
                            ? " bilda-menu__item--danger"
                            : ""
                    }`}
                    disabled={item.disabled}
                    // Radix passes a select Event; our callback takes none —
                    // fewer params is sound, no cast.
                    onSelect={item.onSelect}
                >
                    {item.icon != null ? (
                        <span className="bilda-menu__icon" aria-hidden="true">
                            {item.icon}
                        </span>
                    ) : null}
                    {item.description == null ? (
                        <span className="bilda-menu__item-label">
                            {item.label}
                        </span>
                    ) : (
                        <span className="bilda-menu__item-label">
                            {item.label}
                            <span className="bilda-menu__item-description">
                                {item.description}
                            </span>
                        </span>
                    )}
                    {item.checked === true ? (
                        <span className="bilda-menu__icon" aria-hidden="true">
                            <Icon name="check" />
                        </span>
                    ) : null}
                </RadixMenu.Item>
            );
        case "submenu":
            return (
                <RadixMenu.Sub key={index}>
                    <RadixMenu.SubTrigger
                        className="bilda-menu__item bilda-menu__subtrigger"
                        disabled={item.disabled}
                    >
                        {item.icon != null ? (
                            <span
                                className="bilda-menu__icon"
                                aria-hidden="true"
                            >
                                {item.icon}
                            </span>
                        ) : null}
                        <span className="bilda-menu__item-label">
                            {item.label}
                        </span>
                        {item.hint != null ? (
                            <span className="bilda-menu__hint">
                                {item.hint}
                            </span>
                        ) : null}
                        <span
                            className="bilda-menu__subtrigger-chevron"
                            aria-hidden="true"
                        >
                            <Icon name="chevron-right" size="xs" />
                        </span>
                    </RadixMenu.SubTrigger>
                    <RadixMenu.Portal>
                        <RadixMenu.SubContent
                            className="bilda-menu__content"
                            sideOffset={SIDE_OFFSET}
                            loop
                        >
                            {item.items.map(renderItem)}
                        </RadixMenu.SubContent>
                    </RadixMenu.Portal>
                </RadixMenu.Sub>
            );
        case "segmented":
            return (
                <div key={index} className="bilda-menu__segmented">
                    {item.icon != null ? (
                        <span className="bilda-menu__icon" aria-hidden="true">
                            {item.icon}
                        </span>
                    ) : null}
                    <span className="bilda-menu__item-label">{item.label}</span>
                    <RadixMenu.RadioGroup
                        className="bilda-menu__segments"
                        value={item.value}
                        onValueChange={item.onChange}
                    >
                        {item.options.map((option) => (
                            <RadixMenu.RadioItem
                                key={option.value}
                                value={option.value}
                                className="bilda-menu__segment"
                                // Picking keeps the menu open: the change
                                // shows at once, and a second pick is near.
                                onSelect={(e) => e.preventDefault()}
                            >
                                {option.label}
                            </RadixMenu.RadioItem>
                        ))}
                    </RadixMenu.RadioGroup>
                </div>
            );
    }
};

export const Menu = ({
    trigger,
    items,
    align = "start",
    side = "bottom",
    open,
    onOpenChange,
    contentClassName,
}: MenuProps) => (
    <RadixMenu.Root open={open} onOpenChange={onOpenChange}>
        <RadixMenu.Trigger asChild>{trigger}</RadixMenu.Trigger>
        <RadixMenu.Portal>
            <RadixMenu.Content
                className={`bilda-menu__content${
                    contentClassName ? ` ${contentClassName}` : ""
                }`}
                align={align}
                side={side}
                sideOffset={SIDE_OFFSET}
                loop
            >
                {items.map(renderItem)}
            </RadixMenu.Content>
        </RadixMenu.Portal>
    </RadixMenu.Root>
);
